import { useState, useCallback } from "react";
import type { ReactNode } from "react";
import { Snackbar, Alert, Slide } from "@mui/material";
import type { SlideProps } from "@mui/material";
import { ToastContext } from "@/context/toastContext";
import type { ToastSeverity } from "@/context/toastContext";

interface ToastProviderProps {
  children: ReactNode;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

const severityStyles: Record<ToastSeverity, { bg: string; color: string; border: string }> = {
  success: { bg: "#43a047", color: "#fff", border: "#2e7d32" },
  error: { bg: "#d32f2f", color: "#fff", border: "#b71c1c" },
  warning: { bg: "#ED6F2D", color: "#fff", border: "#c45a1f" },
  info: { bg: "#1565c0", color: "#fff", border: "#0d47a1" },
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<ToastSeverity>("success");

  const showToast = useCallback((msg: string, sev: ToastSeverity = "success") => {
    setOpen(false);
    setTimeout(() => {
      setMessage(msg);
      setSeverity(sev);
      setOpen(true);
    }, 100);
  }, []);

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const styles = severityStyles[severity];

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
        sx={{ mb: 1, mr: 1 }}
      >
        <Alert
          severity={severity}
          onClose={handleClose}
          variant="filled"
          sx={{
            minWidth: 320,
            maxWidth: 480,
            fontSize: "0.95rem",
            fontFamily: "Kanit, sans-serif",
            fontWeight: 500,
            py: 1.25,
            px: 2,
            borderRadius: 2,
            backgroundColor: styles.bg,
            color: styles.color,
            border: `1px solid ${styles.border}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.12)",
            "& .MuiAlert-icon": {
              fontSize: "1.4rem",
              alignItems: "center",
              color: styles.color,
              opacity: 1,
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
              "& .MuiButtonBase-root": {
                color: styles.color,
                opacity: 0.85,
                "&:hover": { opacity: 1 },
              },
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
